import AwesomeDebouncePromise from 'awesome-debounce-promise'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, TextField } from 'react-md'
import styled from 'styled-components'
import DatePickerISO from 'ui/components/DatePickerISO'
import DropdownField from 'ui/components/DropdownField'
import type { PurchaseInvoiceFilters } from './types'
import { PurchaseInvoiceStatus } from '../enum'
import {
	purchaseInvoiceStateFromStatus,
	purchaseInvoiceStatusFromState,
	purchaseInvoiceStatusMessage,
} from '../utils/messages'

const Container = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 16px;
	margin-bottom: 16px;
`

const SearchTextInput = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

const Filter = styled.div`
	min-width: 160px;
`

type Props = {
	filters: PurchaseInvoiceFilters
	setFilters: React.Dispatch<React.SetStateAction<PurchaseInvoiceFilters>>
}

const searchTextDebounced = AwesomeDebouncePromise((func) => {
	func()
}, 1000)

const Filters = ({ filters, setFilters }: Props) => {
	const intl = useIntl()
	const [searchText, setSearchText] = useState<string>()

	useEffect(() => {
		if (searchText !== undefined) {
			searchTextDebounced(() => {
				setFilters((prev) => ({ ...prev, search: searchText }))
			})
		}
	}, [searchText, setFilters])

	// const companyOptions = [] //TODO:
	const statusOptions = Object.keys(PurchaseInvoiceStatus)
		.map((key) => ({
			value: PurchaseInvoiceStatus[key],
			label: purchaseInvoiceStatusMessage(intl, PurchaseInvoiceStatus[key]),
		}))
		.filter(
			(option) =>
				option.value !== PurchaseInvoiceStatus.EXPIRED &&
				option.value !== PurchaseInvoiceStatus.PARTLY_PAID &&
				option.value !== PurchaseInvoiceStatus.INVALID,
		)

	return (
		<Container>
			{/* TODO: Filter by company <Filter>
				<DropdownField
					label={intl.formatMessage({
						defaultMessage: 'Yritys',
						description: 'Finance purchase invoice filter label for company.',
					})}
					itemLabel={'label'}
					itemValue={'value'}
					options={companyOptions}
					hasSelectAll={true}
					nullSelectAll={true}
					value={filters.companyUUID}
					_onOptionSelect={(value) => {
						setFilters((prev) => ({ ...prev, companyUUID: value }))
					}}
				/>
			</Filter> */}

			<Filter>
				<DropdownField
					label={intl.formatMessage({
						defaultMessage: 'Laskun tila',
						description: 'Finance purchase invoice filter label for status.',
					})}
					itemLabel={'label'}
					itemValue={'value'}
					options={statusOptions}
					hasSelectAll={true}
					nullSelectAll={true}
					value={
						filters.state
							? purchaseInvoiceStatusFromState(filters.state, new Date())
							: null
					}
					_onOptionSelect={(value) => {
						setFilters((prev) => {
							if (value === null) {
								const val = { ...prev }
								delete val.state
								return val
							}
							return {
								...prev,
								state: purchaseInvoiceStateFromStatus(value),
							}
						})
					}}
				/>
			</Filter>

			<Filter>
				<DatePickerISO
					id="purchase-invoice-begin-date-filter"
					label={intl.formatMessage({
						defaultMessage: 'Alkupäivä',
						description:
							'Finance purchase invoice filter label for begin date.',
					})}
					value={filters['date[gte]'] || ''}
					onChange={(isoDate: string) => {
						setFilters((prev) => ({ ...prev, 'date[gte]': isoDate }))
					}}
				/>
			</Filter>

			<Filter>
				<DatePickerISO
					id="purchase-invoice-end-date-filter"
					label={intl.formatMessage({
						defaultMessage: 'Loppupäivä',
						description: 'Finance purchase invoice filter label for end date.',
					})}
					value={filters['date[lte]'] || ''}
					onChange={(isoDate: string) => {
						setFilters((prev) => ({ ...prev, 'date[lte]': isoDate }))
					}}
				/>
			</Filter>

			<Filter>
				<SearchTextInput>
					<TextField
						id="purchase-invoice-search-text-input"
						label={intl.formatMessage({
							defaultMessage: 'Hakusana',
							description: 'Label for the payment product search text filter.',
						})}
						value={searchText || ''}
						onChange={(value) => {
							setSearchText(value.toString())
						}}
					/>
					<Button
						icon
						style={{ marginTop: 16 }}
						onClick={() => {
							setSearchText('')
						}}
					>
						clear
					</Button>
				</SearchTextInput>
			</Filter>
		</Container>
	)
}

export default Filters
